import type { FrameRecords, SideDisplayConfig } from "../types/api";
import { visibleColumns, fmtCell, sigPosMap } from "./columns";
import { handMatchResidual } from "./handMatchSign";
import type { HandMatchSignRules } from "./handMatchSign";

// Resolve a manual-match content signature (`_sig`) into a human-readable one-line
// label built from the SAME curated display columns the grids show — so the applied /
// remembered / suggested lists read like the rows they refer to (a reference, a date,
// an amount) instead of the raw opaque signature string. Display-only.
//
// Columns are chosen from the side's display_config lead order, skipping match/category
// bookkeeping and the amount (the amount is shown separately, formatted as AED). The
// per-frame sig→row index is cached on the frame object (frames are replaced wholesale
// on every re-finalize, so a WeakMap keyed on the frame can never go stale).

// Cached per (frame, sig column). The column is part of the key because the same frame
// can be addressed by more than one signature: engine frames carry "_sig", while the
// prior-period carried frame carries "unrec_sig" (its own durable content key). Caching
// on the frame alone would serve the wrong map to the second caller.
const SIG_ROW_CACHE = new WeakMap<FrameRecords, Map<string, Map<string, unknown[]>>>();

function sigRowMap(frame: FrameRecords, sigColumn: string): Map<string, unknown[]> {
  let byCol = SIG_ROW_CACHE.get(frame);
  if (!byCol) {
    byCol = new Map();
    SIG_ROW_CACHE.set(frame, byCol);
  }
  let m = byCol.get(sigColumn);
  if (!m) {
    const si = frame.columns.indexOf(sigColumn);
    const built = new Map<string, unknown[]>();
    if (si >= 0) {
      for (const r of frame.rows) {
        const k = String(r[si] ?? "");
        if (k && !built.has(k)) built.set(k, r);
      }
    }
    byCol.set(sigColumn, built);
    m = built;
  }
  return m;
}

// THE OTHER LEG(S) of a match, in the opposite book.
//
// A match has two sides, and a panel that offers to UN-match one while showing only the
// side the user's search happened to land on is asking them to authorise a change they
// cannot check: whether the app tied the right two transactions is a judgement about the
// PAIR (do the amounts offset? is it the same policy/party?), never about one leg. Finance
// pasted insurer "Accounting Ref." values and were shown insurer rows only, with no way to
// see the CRI row that would be freed alongside.
//
// The link is `matched_row_id`, the same pointer the engine's own release closure walks
// (backend/reconcile.py::_release_matches):
//   - forward  — the counterpart THIS row names, in the other frame;
//   - backward — rows in the other frame naming THIS one, which is how an aggregated N:M
//                group hangs together (every leg points at the group's anchor).
// Both directions are needed or an aggregated group reports only part of what a release
// would free. Deliberately NOT expanded through match_group_id: those ids are
// content-derived and shared by independent matches, so expanding on one would name rows
// the release will not touch — the same trap that caused a real over-release on GIG.
//
// Returns [] when the row is unmatched, when the pointer resolves to nothing (a stale
// index after a re-parse), or when the frame carries no pointer column — a caller then
// says nothing rather than asserting a counterpart it cannot prove.

// Pointer indices arrive from pandas as FLOATS (201.0), while `indices` carries ints.
// Compare on a normalised key or every lookup silently misses.
function idKey(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isFinite(n)) return String(Math.trunc(n));
  const s = String(v).trim();
  return s && s !== "nan" && s !== "None" && s !== "NaT" ? s : null;
}

// Cached per frame: index key -> row position, and pointer key -> positions pointing AT it.
const PTR_CACHE = new WeakMap<FrameRecords, {
  byIndex: Map<string, number>;
  byPointer: Map<string, number[]>;
}>();

function pointerMaps(frame: FrameRecords) {
  let m = PTR_CACHE.get(frame);
  if (m) return m;
  const byIndex = new Map<string, number>();
  for (let i = 0; i < frame.indices.length; i++) {
    const k = idKey(frame.indices[i]);
    if (k !== null && !byIndex.has(k)) byIndex.set(k, i);
  }
  const byPointer = new Map<string, number[]>();
  const pi = frame.columns.indexOf("matched_row_id");
  if (pi >= 0) {
    for (let i = 0; i < frame.rows.length; i++) {
      const k = idKey(frame.rows[i][pi]);
      if (k === null) continue;
      const at = byPointer.get(k);
      if (at) at.push(i); else byPointer.set(k, [i]);
    }
  }
  m = { byIndex, byPointer };
  PTR_CACHE.set(frame, m);
  return m;
}

/** Signatures of the rows in `other` that belong to the same match as `sig` in `frame`. */
export function counterpartSigs(
  frame: FrameRecords,
  other: FrameRecords | undefined,
  sig: string,
): string[] {
  if (!other || !sig) return [];
  const si = frame.columns.indexOf("_sig");
  const osi = other.columns.indexOf("_sig");
  if (si < 0 || osi < 0) return [];

  const opp = pointerMaps(other);

  // Locate this row and its own frame index. Through the cached sig->position map, not
  // a scan: this is called once per matched hit from inside ColumnFilterGrid's render,
  // so on a 62k-row frame a panel listing 20 hits walked ~1.2M rows per render. The map
  // is built once per frame and takes the same first-position-wins rule the scan did.
  const pos = sigPosMap(frame).get(sig);
  if (pos === undefined) return [];
  const ownKey = idKey(frame.indices[pos]);

  const out: string[] = [];
  const seen = new Set<string>();
  const add = (p: number) => {
    const s = String(other.rows[p][osi] ?? "");
    if (s && !seen.has(s)) { seen.add(s); out.push(s); }
  };

  // Forward: the counterpart this row names.
  const pi = frame.columns.indexOf("matched_row_id");
  if (pi >= 0) {
    const k = idKey(frame.rows[pos][pi]);
    if (k !== null) {
      const at = opp.byIndex.get(k);
      if (at !== undefined) add(at);
    }
  }
  // Backward: rows naming this one (an aggregated group's other legs).
  if (ownKey !== null) {
    for (const p of opp.byPointer.get(ownKey) ?? []) add(p);
  }
  return out;
}

// Column keys that don't belong in a compact label (status/category bookkeeping,
// and the amount which the caller renders separately as AED).
const LABEL_SKIP = new Set([
  "match_status", "recon_category", "cri_amount", "balance_amount", "signed_amount",
]);

// Up to this many descriptive fields in the label (keeps it one readable line).
const MAX_LABEL_FIELDS = 3;

// Which BOOK a signature belongs to. Engine signatures are self-identifying by prefix
// ("CRI|" / "INS|" — backend/reconcile.py), which is the only reliable rule: a released
// same-book group has both legs on one side, so the grid a leg is rendered under does
// not tell you its book. Finance needs the side named explicitly to read a pair at all.
// Returns "" for the carried frame's own "UNREC|" keys and anything unrecognised, so a
// caller can omit the badge rather than assert a wrong side.
export function sigSide(sig: string | null | undefined): "CRI" | "Insurer" | "" {
  const s = String(sig ?? "");
  if (s.startsWith("INS|")) return "Insurer";
  if (s.startsWith("CRI|")) return "CRI";
  return "";
}

// The row's signed amount, or null when the frame carries no amount column for it.
//
// sigLabel deliberately OMITS the amount (LABEL_SKIP) because its original callers render
// it separately as AED. The release panels have no such separate field, and the amount is
// the first thing finance checks when judging whether the app tied the right two
// transactions — a pair is only credible if the two sides offset. Reuses the same cached
// sig->row map as sigLabel, so naming the amount costs no extra scan.
//
// CRI carries "cri_amount", the insurer book "balance_amount"; "signed_amount" is the
// shared fallback. Returns null (never 0) when nothing resolves, so a caller can omit the
// figure rather than print a fabricated zero.
export function sigAmount(
  frame: FrameRecords,
  sig: string | null,
  sigColumn: string = "_sig",
): number | null {
  if (!sig) return null;
  const row = sigRowMap(frame, sigColumn).get(sig);
  if (!row) return null;
  for (const key of ["cri_amount", "balance_amount", "signed_amount"]) {
    const i = frame.columns.indexOf(key);
    if (i < 0) continue;
    const v = Number(row[i]);
    if (row[i] !== null && row[i] !== "" && Number.isFinite(v)) return v;
  }
  return null;
}

// IDENTICAL COPIES. Rows identical in every statement column can only be told apart by
// their order in the file, so their row key ends "#k" (backend/row_signatures._row_keys).
// A saved match naming SOME of them may attach to another copy in a later month, or not
// apply; finance asked to be warned when only some are ticked (2026-09-28).
const NUMBERED_KEY = /#\d+$/;
const COPY_COUNT_CACHE = new WeakMap<FrameRecords, Map<string, number>>();

export interface PartialCopies { sig: string; total: number; ticked: number }

/** Ticked rows of `frame` (the WHOLE book) with identical copies not all ticked. */
export function partialIdenticalCopies(frame: FrameRecords, picks: string[]): PartialCopies[] {
  const numbered = picks.filter((s) => NUMBERED_KEY.test(s));
  if (!numbered.length) return [];
  let counts = COPY_COUNT_CACHE.get(frame);
  if (!counts) {
    counts = new Map();
    const si = frame.columns.indexOf("_sig");
    if (si >= 0) {
      for (const r of frame.rows) {
        const k = String(r[si] ?? "");
        if (!NUMBERED_KEY.test(k)) continue;
        const base = k.replace(NUMBERED_KEY, "");
        counts.set(base, (counts.get(base) ?? 0) + 1);
      }
    }
    COPY_COUNT_CACHE.set(frame, counts);
  }
  const ticked = new Map<string, PartialCopies>();
  for (const s of numbered) {
    const base = s.replace(NUMBERED_KEY, "");
    const t = ticked.get(base);
    if (t) t.ticked++;
    else ticked.set(base, { sig: s, total: counts.get(base) ?? 1, ticked: 1 });
  }
  return [...ticked.values()].filter((c) => c.ticked < c.total);
}

// match_group_id prefixes of the prior-period passes (backend/carried_link.py _CARRIED_GID
// and _SETTLED_BEFORE_GID). Those rows are matched AFTER releases apply: a "PP:" pair is
// undone through its item (priorSettlementOf); a released "SB:" row is withheld before
// its pair is made (carried_link.withhold_undone_settled_before), so it takes Release.
export const PRIOR_PERIOD_GIDS = ["PP:", "SB:"];

/** True when the row `sig` names was matched by the settled-before-period pass. */
export function isSettledBeforeMatch(frame: FrameRecords, sig: string): boolean {
  return matchGroupIdOf(frame, sig).startsWith(PRIOR_PERIOD_GIDS[1]);
}

/** The match_group_id of the row `sig` names, or "". */
export function matchGroupIdOf(frame: FrameRecords, sig: string): string {
  const gi = frame.columns.indexOf("match_group_id");
  if (gi < 0 || !sig) return "";
  const row = sigRowMap(frame, "_sig").get(sig);
  return row ? String(row[gi] ?? "") : "";
}

/** True when the row `sig` names was matched by a prior-period pass. */
export function isPriorPeriodMatch(frame: FrameRecords, sig: string): boolean {
  const gid = matchGroupIdOf(frame, sig);
  return PRIOR_PERIOD_GIDS.some((p) => gid.startsWith(p));
}

// What one applied/remembered match adds up to.
//
// SAME CONVENTION as the basket preview in ManualMatchTab and the engine's group tie gate
// (backend.reconcile._apply_manual_matches): cross-book legs must cancel under `rules`
// (handMatchResidual; magnitudes where no convention is stated), a within-ledger offset
// nets the signed amounts. Each side nets first: a per-row sum of magnitudes would
// double-count a side holding both a debit and a credit and invent a gap.
//
// `signMismatch`: the sides agree on magnitude but not on sign, so the match did not settle
// (the engine put it on Group 3); the footing says why instead of reading as a tie.
//
// `resolved` is false as soon as ONE leg is absent from the frames (a remembered match
// that didn't apply this run). Every total is then a partial sum, so `ties` is forced
// false — a group missing a leg must never report that it settles exactly.
export interface MatchGroupTotals {
  crossBook: boolean;
  criTotal: number;
  insTotal: number;
  gap: number;
  net: number;
  residual: number;
  ties: boolean;
  resolved: boolean;
  legs: number;
  signMismatch: boolean;
}

// Below this the two sides are the same figure to the fils — the engine's own exact-tie
// epsilon. Anything larger is reported as a residual, never as a verdict: the Matched
// tolerance is per track and is not in this payload.
const TIE_EPS = 0.005;

export function matchGroupTotals(
  criSigs: string[],
  insSigs: string[],
  criFrame: FrameRecords,
  insFrame: FrameRecords,
  rules?: HandMatchSignRules | null,
): MatchGroupTotals {
  let resolved = true;
  const amounts = (sigs: string[], frame: FrameRecords) =>
    sigs.map((s) => {
      const v = sigAmount(frame, s);
      if (v === null) resolved = false;
      return v ?? 0;
    });
  const criAmts = amounts(criSigs, criFrame);
  const insAmts = amounts(insSigs, insFrame);
  const criNet = criAmts.reduce((a, b) => a + b, 0);
  const insNet = insAmts.reduce((a, b) => a + b, 0);
  const crossBook = criSigs.length > 0 && insSigs.length > 0;
  const criTotal = crossBook ? Math.abs(criNet) : criNet;
  const insTotal = crossBook ? Math.abs(insNet) : insNet;
  const magnitudeGap = Math.abs(criTotal - insTotal);
  const ti = insFrame.columns.indexOf("track");
  const insTracks = insSigs.map((s) => {
    const row = sigRowMap(insFrame, "_sig").get(s);
    return row && ti >= 0 ? row[ti] : "";
  });
  const gap = crossBook ? handMatchResidual(criAmts, insAmts, insTracks, rules) : magnitudeGap;
  const net = criTotal + insTotal;
  const residual = crossBook ? gap : Math.abs(net);
  return {
    crossBook, criTotal, insTotal, gap, net, residual,
    ties: resolved && residual <= TIE_EPS,
    resolved,
    legs: criSigs.length + insSigs.length,
    signMismatch: crossBook && resolved && magnitudeGap <= TIE_EPS && residual > TIE_EPS,
  };
}

// `sigColumn` names the column holding the signature — "_sig" on the engine frames,
// "unrec_sig" on the prior-period carried frame. Defaults to "_sig", so every existing
// caller is unaffected.
export function sigLabel(
  frame: FrameRecords,
  sig: string | null,
  config?: SideDisplayConfig,
  sigColumn: string = "_sig",
): string {
  if (!sig) return "—";
  const row = sigRowMap(frame, sigColumn).get(sig);
  if (!row) return sig; // fall back to the raw signature if the row isn't in this frame
  const cols = visibleColumns(frame, config).filter((c) => !LABEL_SKIP.has(c.key));
  const parts: string[] = [];
  // DISTINCT values only. Several display columns routinely carry the SAME string — on
  // GIG's insurer book tran_code and doc_reference are both "94DN04464469" — and a label
  // capped at three fields that spends two of them on one repeated value reads as a bug
  // and, worse, never reaches the fields that actually identify the row (the policy
  // number, the assured name). Compare case-insensitively on the DISPLAYED text, which is
  // what the reader is deduplicating by eye.
  const seen = new Set<string>();
  for (const c of cols) {
    const cell = fmtCell(row[c.index], c.key);
    if (!cell || cell === "—") continue;
    const k = cell.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    parts.push(cell);
    if (parts.length >= MAX_LABEL_FIELDS) break;
  }
  return parts.length ? parts.join(" · ") : sig;
}
